"use client";

import { useState, useMemo } from "react";
import { Input, Switch, Accordion, AccordionItem } from "@heroui/react";
import {
  IconHelpSquareRounded,
  IconCards,
  IconEyeSearch,
  IconTargetArrow,
  IconMathEqualGreater,
} from "@tabler/icons-react";
var hypergeometric = require("@stdlib/stats-base-dists-hypergeometric");
import "./hyper-geo-calc.css";
import { AgGridReact } from "ag-grid-react"; // React Data Grid Component
import "ag-grid-community/styles/ag-grid.css"; // Mandatory CSS required by the grid
import "ag-grid-community/styles/ag-theme-quartz.css"; // Optional Theme applied to the grid
import "ag-grid-community/styles/ag-theme-alpine.css"; // Optional Theme applied to the grid

function hyper() {
  return 0;
}

function getPercent(number: number): string {
  return (Math.round(number * 1000) / 10).toFixed(1);
}

function addS(number: number) {
  if (number === 1) {
    return "";
  } else {
    return "s";
  }
}

function getExactSuccessesData(
  deckSize: number,
  cardsDrawn: number,
  hits: number,
  hitsInDraw: number,
  //   mulligan: boolean,
) {
  let exactSuccessData = [];
  const cardsInFirstDraw = 5;
  //   if (mulligan) {
  //     for (let i = 0; i < Math.min(cardsDrawn + 1, hits + 1); i++) {
  //       const firstDrawSuccess = hypergeometric.pmf(
  //         i,
  //         deckSize,
  //         hits,
  //         cardsInFirstDraw,
  //       );
  //       // later you must continue to draw no more of that card since you already succeeded
  //       const firstDrawNoMore = hypergeometric.pmf(
  //         0,
  //         deckSize - cardsInFirstDraw,
  //         hits - i,
  //         cardsDrawn - cardsInFirstDraw,
  //       );

  //       console.log("firstDrawNoMore", {
  //         expected: 0,
  //         deckSize: deckSize - cardsInFirstDraw,
  //         hits: hits - i,
  //         cardsDrawn: cardsDrawn - cardsInFirstDraw,
  //         firstDrawNoMore: firstDrawNoMore,
  //       });

  //       const firstDrawFail = 1 - firstDrawSuccess;
  //       const laterSuccess = hypergeometric.pmf(i, deckSize, hits, cardsDrawn);
  //       console.log(i, "firstDrawSuccess", firstDrawSuccess);
  //       console.log(i, "firstDrawNoMore", firstDrawNoMore);
  //       console.log(i, "firstDrawFail", firstDrawFail);
  //       console.log(i, "laterSuccess", laterSuccess);

  //       exactSuccessData.push({
  //         drawExactly: i,
  //         chance:
  //           getPercent(
  //             firstDrawSuccess * firstDrawNoMore + firstDrawFail * laterSuccess,
  //           ) + "%",
  //       });
  //     }
  //   } else {
  for (let i = 0; i < Math.min(cardsDrawn + 1, hits + 1); i++) {
    exactSuccessData.push({
      drawExactly: i,
      chance:
        getPercent(hypergeometric.pmf(i, deckSize, hits, cardsDrawn)) + "%",
    });
    // }
  }

  return exactSuccessData.reverse();
}

export default function HyperGeoCalc() {
  const defaultSettings = {
    deckSize: "50",
    cardsDrawn: "5",
    hits: "4",
    hitsInDraw: "1",
  };
  const [settings, setSettings] = useState(defaultSettings);

  function setSetting(id: string, value: any) {
    setSettings((prevState) => ({
      ...prevState,
      [id]: value,
    }));
  }

  let exactSuccessData = getExactSuccessesData(
    parseInt(settings.deckSize),
    parseInt(settings.cardsDrawn),
    parseInt(settings.hits),
    parseInt(settings.hitsInDraw),
  );

  const textValues = {
    deckSize: {
      title: "Cards in Deck",
      icon: <IconCards />,
      text: "The number of cards in the total population. At the start of the game this would be 50.",
    },
    cardsDrawn: {
      title: "Cards Seen",
      icon: <IconEyeSearch />,
      text: "The number of cards you are drawing or searching through. Your starting hand is 5 cards going first or 6 cards going second.",
    },
    hits: {
      title: "Hits",
      icon: <IconTargetArrow />,
      text: "The number of cards you consider a hit. If you want Moria in your starting hand and you play 4 copies, this would be 4.",
    },
    hitsInDraw: {
      title: "At Least",
      icon: <IconMathEqualGreater />,
      text: "At least how many hits you want to see. This number is typically 1, but if you want to know the probability of seeing at least 2 Gordon's / Raise Max in opening hand you would set this to 2.",
    },
  };

  return (
    <div>
      <h1 className="inline-block text-2xl mb-2 sm:text-4xl font-semibold text-slate-900 tracking-tight dark:text-slate-100">
        One Piece TCG Hypergeometric Calculator
      </h1>
      <div>
        <Accordion isCompact variant="light" className="my-4 px-0">
          <AccordionItem
            key="1"
            aria-label="Leader Distribution"
            title={
              <div className="flex flex-row gap-2 items-center">
                <IconHelpSquareRounded />
                What is this?
              </div>
            }
          >
            <h2 className="text-base md:block mt-2 text-slate-700 dark:text-slate-300 md:text-lg">
              <p>
                We are using the hypergeometric equation to calculate the
                likelihood of drawing specific cards from a One Piece TCG deck.
                This formula accounts for the fact that each draw reduces the
                size of the deck by one, causing the probability of drawing
                subsequent cards to change dynamically with each draw.
              </p>
            </h2>
          </AccordionItem>
        </Accordion>
      </div>
      {/* <div className="text-lg text-slate-100 mt-4">Examples</div> */}
      <div className="hidden md:block">
        <DesktopView
          settings={settings}
          setSetting={setSetting}
          textValues={textValues}
        />
      </div>
      <div className="block md:hidden">
        <MobileView
          settings={settings}
          setSetting={setSetting}
          textValues={textValues}
        />
      </div>

      <div className="mt-10">
        <div className="text-slate-100 text-lg">Results</div>
        <div className="mt-2">
          <span className="text-slate-400">
            Drawing {settings.hitsInDraw} or more hits:{" "}
          </span>
          {getPercent(
            1 -
              hypergeometric.cdf(
                parseInt(settings.hitsInDraw) - 1,
                parseInt(settings.deckSize),
                parseInt(settings.hits),
                parseInt(settings.cardsDrawn),
              ),
          )}
          %
        </div>
        <div className="md:max-w-[400px] mt-4">
          <ExactlyGrid rowData={exactSuccessData} />
        </div>
      </div>
    </div>
  );
}

function ExactlyGrid({ rowData }: { rowData: any }) {
  const autoSizeStrategy = {
    type: "fitGridWidth",
    defaultMinWidth: 100,
    // columnLimits: [
    //   {
    //     colId: "country",
    //     minWidth: 900,
    //   },
    // ],
  };

  // Column Definitions: Defines the columns to be displayed.
  const [colDefs, setColDefs] = useState([
    { field: "drawExactly", headerName: "Draw Exactly {x} Hits" },
    {
      field: "chance",
      headerName: "Chance",
    },
  ]);

  function formatNumber(number: number) {
    return Math.floor(number).toLocaleString();
  }

  const defaultColDef = useMemo<any>(() => {
    return {
      minWidth: 100,
      flex: 1,
      //   cellClass: "align-right",
      //   enableCellChangeFlash: true,
      flashDuration: 10000,
      enableRangeSelection: true,
      //   valueFormatter: (params: any) => {
      //     return formatNumber(params.value);
      //   },
    };
  }, []);

  //   const onCellValueChanged = (event: any) => {
  //     setRowData([...rowData]);
  //   };

  //   const statusBar = useMemo(() => {
  //     return {
  //       statusPanels: [
  //         { statusPanel: "agTotalAndFilteredRowCountComponent" },
  //         { statusPanel: "agTotalRowCountComponent" },
  //         { statusPanel: "agFilteredRowCountComponent" },
  //         { statusPanel: "agSelectedRowCountComponent" },
  //         { statusPanel: "agAggregationComponent" },
  //       ],
  //     };
  //   }, []);
  return (
    <div
      className="ag-theme-alpine-dark" // applying the grid theme
      // style={{ height: 302 }} // the grid will fill the size of the parent container
    >
      <AgGridReact
        // statusBar={statusBar}
        domLayout="autoHeight"
        rowData={rowData}
        columnDefs={colDefs}
        defaultColDef={defaultColDef}
        // onCellValueChanged={onCellValueChanged}
        autoSizeStrategy={autoSizeStrategy as any}
        // rowSelection="multiple"
        // enableRangeSelection={true}
      />
    </div>
  );
}

interface Settings {
  deckSize: string;
  cardsDrawn: string;
  hits: string;
  hitsInDraw: string;
}

function DesktopView({
  settings,
  setSetting,
  textValues,
}: {
  settings: Settings;
  setSetting: any;
  textValues: any;
}) {
  return (
    <div className="mt-10 grid grid-cols-2 gap-4 items-center text-slate-100">
      <DesktopItem textValue={textValues.deckSize} />
      <Input
        size="lg"
        type="number"
        value={settings.deckSize}
        className="max-w-[100px]"
        onValueChange={(val: any) => setSetting("deckSize", val)}
      />
      <DesktopItem textValue={textValues.cardsDrawn} />
      <Input
        size="lg"
        type="number"
        value={settings.cardsDrawn}
        className="max-w-[100px]"
        onValueChange={(val: any) => setSetting("cardsDrawn", val)}
      />
      <DesktopItem textValue={textValues.hits} />
      <Input
        size="lg"
        type="number"
        value={settings.hits}
        className="max-w-[100px]"
        onValueChange={(val: any) => setSetting("hits", val)}
      />
      <DesktopItem textValue={textValues.hitsInDraw} />
      <Input
        size="lg"
        type="number"
        value={settings.hitsInDraw}
        className="max-w-[100px]"
        onValueChange={(val: any) => setSetting("hitsInDraw", val)}
      />
      {/* <div>
      <div className="text-lg">Mulligan</div>
      <div className="text-slate-400">
        Include the mulligan in the calculation. Return the chance of
        success in the first 5 cards plus the chance of failure in the
        initial mulligan and success in the following card draw as specified
        above ({cardsDrawn}).
      </div>
    </div>
    <div>
      <div className="w-[100px] flex items-center justify-center">
        <Switch isSelected={mulligan} onValueChange={setMulligan} />
      </div>
    </div> */}
    </div>
  );
}

function DesktopItem({ textValue }: { textValue: any }) {
  return (
    <div className="mt-4 md:mt-0">
      <div className="text-lg flex items-center">
        {textValue.icon}
        <div className="ml-2">{textValue.title}</div>
      </div>
      <div className="text-slate-400">{textValue.text}</div>
    </div>
  );
}

function MobileView({
  settings,
  setSetting,
  textValues,
}: {
  settings: Settings;
  setSetting: any;
  textValues: any;
}) {
  return (
    <div className="grid grid-cols-1 gap-4 items-center text-slate-100">
      <div>
        <AccordionWrapper
          icon={textValues.deckSize.icon}
          title={textValues.deckSize.title}
          text={textValues.deckSize.text}
        />
        <Input
          size="md"
          type="number"
          value={settings.deckSize}
          className="max-w-[100px]"
          onValueChange={(val: any) => setSetting("deckSize", val)}
        />
      </div>
      <div>
        <AccordionWrapper
          icon={textValues.cardsDrawn.icon}
          title={textValues.cardsDrawn.title}
          text={textValues.cardsDrawn.text}
        />
        <Input
          size="md"
          type="number"
          value={settings.cardsDrawn}
          className="max-w-[100px]"
          onValueChange={(val: any) => setSetting("cardsDrawn", val)}
        />
      </div>
      <div>
        <AccordionWrapper
          icon={textValues.hits.icon}
          title={textValues.hits.title}
          text={textValues.hits.text}
        />

        <Input
          size="md"
          type="number"
          value={settings.hits}
          className="max-w-[100px]"
          onValueChange={(val: any) => setSetting("hits", val)}
        />
      </div>
      <div>
        <AccordionWrapper
          icon={textValues.hitsInDraw.icon}
          title={textValues.hitsInDraw.title}
          text={textValues.hitsInDraw.text}
        />

        <Input
          size="md"
          type="number"
          value={settings.hitsInDraw}
          className="max-w-[100px]"
          onValueChange={(val: any) => setSetting("hitsInDraw", val)}
        />
      </div>
      {/* <div>
        <div className="text-lg">Mulligan</div>
        <div className="text-slate-400">
          Include the mulligan in the calculation. Return the chance of
          success in the first 5 cards plus the chance of failure in the
          initial mulligan and success in the following card draw as specified
          above ({cardsDrawn}).
        </div>
      </div>
      <div>
        <div className="w-[100px] flex items-center justify-center">
          <Switch isSelected={mulligan} onValueChange={setMulligan} />
        </div>
      </div> */}
    </div>
  );
}

function AccordionWrapper({
  icon,
  title,
  text,
}: {
  icon: any;
  title: string;
  text: string;
}) {
  return (
    <Accordion isCompact variant="light" className="px-0">
      <AccordionItem
        key="1"
        aria-label="Leader Distribution"
        title={
          <div className="flex flex-row gap-2 items-center">
            {icon}
            {title}
          </div>
        }
      >
        <h2 className="text-base md:block mb-2 text-slate-700 dark:text-slate-300 md:text-lg">
          <p>{text}</p>
        </h2>
      </AccordionItem>
    </Accordion>
  );
}
